import type {RefObject, ReactElement, ElementType} from 'react';
import {useRef} from 'react';
import {useMenuTriggerState} from '@react-stately/menu';
import {DismissButton, Overlay, usePopover} from '@react-aria/overlays';
import {useMenuTrigger} from '@react-aria/menu';
import type {MenuTriggerState} from '@react-stately/menu';
import type {AriaPopoverProps} from '@react-aria/overlays';
import type {FocusableElement} from '@react-types/shared';
import type {AriaButtonProps} from '@react-aria/button';
import type {AriaMenuProps} from '@react-types/menu';

import type {SubMenusDict} from './menu.types';
import {Menu} from './menu';

type Props<TriggerType extends ElementType = 'button'> = AriaMenuProps<object> &
Pick<AriaPopoverProps, 'placement'> & {
    renderButton: (
        ref: RefObject<FocusableElement>,
        buttonProps: AriaButtonProps<ElementType>,
        state: MenuTriggerState
    ) => ReactElement<unknown, TriggerType>;
    className?: string;
    subMenus?: SubMenusDict<object>;
};

export function MenuButton({renderButton, placement, className, subMenus, ...props}: Props) {
    const triggerRef = useRef<FocusableElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const state = useMenuTriggerState({trigger: 'press'});

    const {menuTriggerProps, menuProps} = useMenuTrigger<object>({type: 'menu'}, state, triggerRef);

    const {popoverProps} = usePopover(
        {
            placement,
            isNonModal: true,
            popoverRef,
            triggerRef,
        },
        state,
    );

    return (
        <>
            {renderButton(triggerRef, menuTriggerProps, state)}
            {state.isOpen && (
                <Overlay>
                    <div {...popoverProps} ref={popoverRef}>
                        <DismissButton onDismiss={state.close} />
                        <Menu
                            {...menuProps}
                            {...props}
                            className={className}
                            autoFocus={state.focusStrategy || true}
                            onClose={state.close}
                            subMenus={subMenus}
                        />
                    </div>
                </Overlay>
            )}
        </>
    );
}
